import { useRef, useEffect } from "react";
import debounce from "lodash/debounce";
import { logger } from "../utils/logger";

/**
 * Returns a log(key, message, meta) function for noisy, rapid-fire events
 * (keystrokes, filter typing). Calls sharing the same `key` are debounced
 * together so only the last one within `delay` ms actually gets logged;
 * calls with different keys debounce independently, so editing field A then
 * field B in the same form still logs both instead of only the last touched.
 *
 * Any debounce still pending when the component unmounts is flushed
 * immediately so an in-progress edit isn't silently dropped.
 */
export const useDebouncedLogger = (delay = 700) => {
  const debouncedFns = useRef({});

  useEffect(() => {
    const fns = debouncedFns.current;
    return () => {
      Object.values(fns).forEach((fn) => fn.flush());
    };
  }, []);

  const log = (key, message, meta) => {
    if (!debouncedFns.current[key]) {
      debouncedFns.current[key] = debounce(
        (msg, m) => logger.info(msg, m),
        delay
      );
    }
    debouncedFns.current[key](message, meta);
  };

  return log;
};
